import { canonicalJson, contractError, validateJsonObject, type JsonValue } from './json.js';

/** Closed, data-only schema vocabulary. No executable custom schema callbacks
 * can be supplied by a creation or a model. Catalog refinements are platform code. */
export type ConfigSchema =
  | { type: 'number'; min: number; max: number; integer?: boolean }
  /** `signal` marks a string that names a signal kind: one the module
   * REACTS to (`reads`, checked against what the game's modules raise) or
   * one it RAISES (`raises`). */
  | { type: 'string'; maxLength: number; pattern?: 'id'; values?: readonly string[]; signal?: 'reads' | 'raises' }
  | { type: 'boolean' }
  | { type: 'array'; items: ConfigSchema; minItems?: number; maxItems: number; unique?: boolean }
  | { type: 'object'; properties: Readonly<Record<string, ConfigSchema>>; required: readonly string[] }
  | { type: 'union'; variants: readonly ConfigSchema[] };

/** Limits for schemas supplied as release data, rather than platform code. */
export const CONFIG_SCHEMA_LIMITS = Object.freeze({ depth: 8, nodes: 256,
  properties: 32, variants: 8, arrayItems: 256, stringLength: 1024, enumValues: 64 });

/** Validate the schema itself before treating it as policy. No arbitrary regex,
 * references, executable predicates, additional properties or unbounded types. */
export function validateConfigSchema(value: unknown, path = 'schema'): ConfigSchema {
  const input = validateJsonObject(value, path);
  let nodes = 0;
  function walk(raw: JsonValue, itemPath: string, depth: number): ConfigSchema {
    if (++nodes > CONFIG_SCHEMA_LIMITS.nodes || depth > CONFIG_SCHEMA_LIMITS.depth) {
      contractError(itemPath, 'schema complexity limit exceeded');
    }
    if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) contractError(itemPath, 'expected schema object');
    const only = (keys: readonly string[]) => {
      for (const key of Object.keys(raw)) if (!keys.includes(key)) contractError(`${itemPath}.${key}`, 'unsupported schema field');
    };
    const boundedInt = (entry: JsonValue | undefined, label: string, min: number, max: number): number => {
      if (typeof entry !== 'number' || !Number.isSafeInteger(entry) || entry < min || entry > max) {
        contractError(`${itemPath}.${label}`, `expected integer in [${min}, ${max}]`);
      }
      return entry;
    };
    const boolean = (key: string) => {
      if (raw[key] !== undefined && typeof raw[key] !== 'boolean') contractError(`${itemPath}.${key}`, 'expected boolean');
    };
    switch (raw.type) {
      case 'number': {
        only(['type', 'min', 'max', 'integer']);
        boolean('integer');
        const { min, max } = raw;
        if (typeof min !== 'number' || typeof max !== 'number' || !Number.isFinite(min) || !Number.isFinite(max)
          || Math.abs(min) > Number.MAX_SAFE_INTEGER || Math.abs(max) > Number.MAX_SAFE_INTEGER || min > max
          || (raw.integer === true && Math.ceil(min) > Math.floor(max))) contractError(itemPath, 'expected a satisfiable finite number interval');
        return { type: 'number', min, max, ...(raw.integer === undefined ? {} : { integer: raw.integer as boolean }) };
      }
      case 'string': {
        only(['type', 'maxLength', 'pattern', 'values', 'signal']);
        if (raw.signal !== undefined && raw.signal !== 'reads' && raw.signal !== 'raises') contractError(`${itemPath}.signal`, 'expected reads or raises');
        const maxLength = boundedInt(raw.maxLength, 'maxLength', 1, CONFIG_SCHEMA_LIMITS.stringLength);
        if (raw.pattern !== undefined && raw.pattern !== 'id') contractError(`${itemPath}.pattern`, 'only the built-in id pattern is supported');
        let values: string[] | undefined;
        if (raw.values !== undefined) {
          if (!Array.isArray(raw.values) || raw.values.length < 1 || raw.values.length > CONFIG_SCHEMA_LIMITS.enumValues) contractError(`${itemPath}.values`, 'expected a bounded non-empty enum');
          values = raw.values.map((entry, index) => {
            if (typeof entry !== 'string' || entry.length < 1 || entry.length > maxLength
              || (raw.pattern === 'id' && !/^[a-zA-Z0-9][a-zA-Z0-9_.:-]*$/.test(entry))) contractError(`${itemPath}.values[${index}]`, 'enum entry violates the string schema');
            return entry;
          });
          if (new Set(values).size !== values.length) contractError(`${itemPath}.values`, 'duplicate enum entries');
        }
        return { type: 'string', maxLength, ...(raw.pattern === undefined ? {} : { pattern: 'id' as const }), ...(values === undefined ? {} : { values }),
          ...(raw.signal === undefined ? {} : { signal: raw.signal as 'reads' | 'raises' }) };
      }
      case 'boolean':
        only(['type']);
        return { type: 'boolean' };
      case 'array': {
        only(['type', 'items', 'minItems', 'maxItems', 'unique']);
        boolean('unique');
        const maxItems = boundedInt(raw.maxItems, 'maxItems', 0, CONFIG_SCHEMA_LIMITS.arrayItems);
        const minItems = raw.minItems === undefined ? undefined : boundedInt(raw.minItems, 'minItems', 0, maxItems);
        if (raw.items === undefined) contractError(`${itemPath}.items`, 'required');
        const items = walk(raw.items, `${itemPath}.items`, depth + 1);
        return { type: 'array', items, maxItems, ...(minItems === undefined ? {} : { minItems }), ...(raw.unique === undefined ? {} : { unique: raw.unique as boolean }) };
      }
      case 'object': {
        only(['type', 'properties', 'required']);
        const propertiesValue = raw.properties;
        if (propertiesValue === null || typeof propertiesValue !== 'object' || Array.isArray(propertiesValue)) contractError(`${itemPath}.properties`, 'expected object');
        if (Object.keys(propertiesValue).length > CONFIG_SCHEMA_LIMITS.properties) contractError(`${itemPath}.properties`, 'too many properties');
        const properties: Record<string, ConfigSchema> = {};
        for (const [key, property] of Object.entries(propertiesValue)) {
          if (!/^[a-zA-Z0-9][a-zA-Z0-9_.:-]{0,63}$/.test(key)) contractError(`${itemPath}.properties`, 'unsupported property identifier');
          properties[key] = walk(property, `${itemPath}.properties.${key}`, depth + 1);
        }
        if (!Array.isArray(raw.required) || raw.required.length > CONFIG_SCHEMA_LIMITS.properties) contractError(`${itemPath}.required`, 'expected bounded array');
        const required = raw.required.map((entry, index) => {
          if (typeof entry !== 'string' || !Object.hasOwn(properties, entry)) contractError(`${itemPath}.required[${index}]`, 'must name a declared property');
          return entry;
        });
        if (new Set(required).size !== required.length) contractError(`${itemPath}.required`, 'duplicate property');
        return { type: 'object', properties, required };
      }
      case 'union': {
        only(['type', 'variants']);
        if (!Array.isArray(raw.variants) || raw.variants.length < 1 || raw.variants.length > CONFIG_SCHEMA_LIMITS.variants) contractError(`${itemPath}.variants`, 'expected bounded non-empty array');
        return { type: 'union', variants: raw.variants.map((variant, index) => walk(variant, `${itemPath}.variants[${index}]`, depth + 1)) };
      }
      default:
        contractError(`${itemPath}.type`, 'unsupported schema type');
    }
  }
  return walk(input, path, 0);
}

export function assertConfigSchema(value: JsonValue, schema: ConfigSchema, path = 'config'): void {
  switch (schema.type) {
    case 'number':
      if (typeof value !== 'number' || !Number.isFinite(value) || value < schema.min || value > schema.max ||
        (schema.integer && !Number.isSafeInteger(value))) contractError(path, `expected ${schema.integer ? 'integer' : 'number'} in [${schema.min}, ${schema.max}]`);
      return;
    case 'string':
      if (typeof value !== 'string' || value.length === 0 || value.length > schema.maxLength ||
        (schema.pattern === 'id' && !/^[a-zA-Z0-9][a-zA-Z0-9_.:-]*$/.test(value)) ||
        (schema.values && !schema.values.includes(value))) contractError(path, `expected ${schema.values?.join(' | ') ?? 'bounded non-empty string'}`);
      return;
    case 'boolean':
      if (typeof value !== 'boolean') contractError(path, 'expected boolean');
      return;
    case 'array':
      if (!Array.isArray(value) || value.length < (schema.minItems ?? 0) || value.length > schema.maxItems) contractError(path, `expected array with ${schema.minItems ?? 0}..${schema.maxItems} entries`);
      if (schema.unique && new Set(value.map(v => canonicalJson(v))).size !== value.length) contractError(path, 'duplicate entries');
      value.forEach((v, i) => assertConfigSchema(v, schema.items, `${path}[${i}]`));
      return;
    case 'object':
      if (value === null || typeof value !== 'object' || Array.isArray(value)) contractError(path, 'expected object');
      for (const key of schema.required) if (!Object.hasOwn(value, key)) contractError(`${path}.${key}`, 'required');
      for (const [key, v] of Object.entries(value)) {
        const field = schema.properties[key];
        if (!field) contractError(`${path}.${key}`, 'unsupported field');
        assertConfigSchema(v, field, `${path}.${key}`);
      }
      return;
    case 'union': {
      const failures: string[] = [];
      for (const variant of schema.variants) {
        try { assertConfigSchema(value, variant, path); return; }
        catch (e) { failures.push((e as Error).message); }
      }
      contractError(path, `no supported shape: ${failures.join('; ')}`);
    }
    default:
      contractError(path, 'unsupported schema type');
  }
}

/** A config schema as one compact line for a model to read (`{ speed: 0.2..40,
 * mode: "a"|"b", tag: id, ticks?: int 1..300, cells: [int 0..9] ×1..8 }`):
 * the same constraints as the schema, every optional field marked `?`. */
export function describeConfigSchema(schema: ConfigSchema): string {
  switch (schema.type) {
    case 'number': return `${schema.integer ? 'int ' : ''}${schema.min}..${schema.max}`;
    case 'string': {
      const base = schema.values ? schema.values.map(v => JSON.stringify(v)).join('|') : schema.pattern === 'id' ? 'id' : `string≤${schema.maxLength}`;
      return schema.signal === 'reads' ? `${base} (a signal kind it reacts to)` : schema.signal === 'raises' ? `${base} (a signal kind it raises)` : base;
    }
    case 'boolean': return 'bool';
    case 'array': return `[${describeConfigSchema(schema.items)}] ×${schema.minItems ?? 0}..${schema.maxItems}${schema.unique ? ' unique' : ''}`;
    case 'object': return `{ ${Object.entries(schema.properties)
      .map(([key, value]) => `${key}${schema.required.includes(key) ? '' : '?'}: ${describeConfigSchema(value)}`).join(', ')} }`;
    case 'union': return schema.variants.map(describeConfigSchema).join(' | ');
  }
}
